import { DataTable } from "@/components/data-table";
import { HeaderCell } from "@/components/header-cell";
import { cn, formatCurrency } from "@/lib/utils";
import type { FundingHistory as FundingHistoryType } from "./types";
import {
  createColumnHelper,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useProduct } from "@/features/product/product-context";
import { useProductOptions } from "@/features/product/product-options";
import { useI18n } from "@/features/i18n";

type FundingHistoryProps = {
  data: FundingHistoryType[];
  isLoading: boolean;
  sorting: SortingState;
  onSort: (state: SortingState) => void;
};

export const FundingHistory = ({
  data,
  isLoading,
  sorting,
  onSort,
}: FundingHistoryProps) => {
  const columns = useColumns();

  return (
    <DataTable
      columns={columns as ColumnDef<FundingHistoryType, unknown>[]}
      data={data}
      isLoading={isLoading}
      sorting={sorting}
      onSort={onSort}
    />
  );
};

const columnHelper = createColumnHelper<FundingHistoryType>();

const useColumns = () => {
  const { setProduct } = useProduct();
  const { products } = useProductOptions();
  const navigate = useNavigate();
  const { formatTradeDate } = useI18n();

  const columns = useMemo(
    () => [
      columnHelper.accessor("timestamp", {
        header: (h) => <HeaderCell header={h}>Time</HeaderCell>,
        cell: (props) => {
          const value = props.getValue();
          return (
            <div className="text-muted-foreground text-xs">
              {formatTradeDate(value)}
            </div>
          );
        },
      }),
      columnHelper.accessor("market", {
        header: (h) => <HeaderCell header={h}>Coin</HeaderCell>,
        cell: (props) => {
          const value = props.getValue();
          
          const handleMarketClick = () => {
            const selectedProduct = products.find((p) => p.id === value);
            if (selectedProduct) {
              setProduct(selectedProduct);
              // Navigate to new market URL (replace history to avoid back button cycling through markets)
              navigate(`/trade/${selectedProduct.id}`, { replace: true });
            }
          };
          
          return (
            <div className="flex items-center gap-2">
              <span 
                className="font-medium cursor-pointer hover:text-primary hover:underline transition-colors"
                onClick={handleMarketClick}
              >
                {value}
              </span>
            </div>
          );
        },
      }),
      columnHelper.accessor("size", {
        header: (h) => (
          <HeaderCell header={h} align="right">
            Size
          </HeaderCell>
        ),
        cell: (props) => {
          const value = props.getValue();
          return (
            <div className="text-right font-medium tabular-nums">
              {formatCurrency(value, 4)}
            </div>
          );
        },
        size: 120,
      }),
      columnHelper.accessor("side", {
        header: (h) => (
          <HeaderCell header={h}>
            Position Side
          </HeaderCell>
        ),
        cell: (props) => {
          const value = props.getValue();
          const isLong = value === "long";
          return (
            <div
              className={cn(
                "font-medium pl-4",
                isLong ? "text-bid-foreground" : "text-ask-foreground"
              )}
            >
              {isLong ? "Long" : "Short"}
            </div>
          );
        },
        size: 120,
      }),
      columnHelper.accessor("payment", {
        header: (h) => (
          <HeaderCell header={h} align="right">
            Funding Fee
          </HeaderCell>
        ),
        cell: (props) => {
          const value = props.getValue();
          const isPositive = value >= 0;

          return (
            <div
              className={cn(
                "text-right font-medium",
                isPositive ? "text-bid-foreground" : "text-ask-foreground"
              )}
            >
              {isPositive ? "+" : "-"}${formatCurrency(Math.abs(value), 2)}
            </div>
          );
        },
      }),
      columnHelper.accessor("fundingRate", {
        header: (h) => (
          <HeaderCell header={h} align="right">
            Rate
          </HeaderCell>
        ),
        cell: (props) => {
          const value = props.getValue();
          const isPositive = value >= 0;
          const ratePercent = value * 100;
          
          return (
            <div className="text-right font-medium">
              {isPositive ? "+" : ""}
              {ratePercent.toFixed(4)}%
            </div>
          );
        },
      }),
    ],
    [setProduct, products, navigate, formatTradeDate]
  );

  return columns;
};

